import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import STATUS from 'constants/status';
import dayjs from 'dayjs';
import { formatFullName } from 'utils/utils';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { getRequest } from 'api/apiClient';
import { DATE_TIME_FORMAT } from 'constants/dates';
import { OpSelect } from 'components/customAntd/DLS/OpSelect/OpSelect';
import { setSelectedVisit } from 'store/slices/visitsSlice';
import { VisitorSearch } from 'types/visitorSearchTypes';
import { useTranslation } from 'react-i18next';
import Loader from 'components/customAntd/Loader';

interface VisitorOption {
    label: string;
    value: string;
    visitId: number;
    visitorId: number;
}

interface SignOutModalProps {
    open: boolean;
    locationIdRef: number;
    onClose: () => void;
    openVisitorsDrawer: () => void;
}

const SignOutModal: React.FC<SignOutModalProps> = ({ open, locationIdRef, onClose, openVisitorsDrawer }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);

    const [visitorOptions, setVisitorOptions] = useState<VisitorOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { t } = useTranslation();

    // Set loading when modal opens
    useEffect(() => {
        if (open) {
            setLoading(true);
            setVisitorOptions([]);
        }
    }, [open]);

    // Fetch visitor options
    useEffect(() => {
        if (!open) return;

        const fetchVisitorOptions = async () => {
            try {
                const visitors = await getRequest(`/orgs/${orgId}/search/visitors`, {
                    siteId: locationIdRef,
                    startDate: dayjs().startOf('day').format(DATE_TIME_FORMAT),
                    endDate: dayjs().endOf('day').format(DATE_TIME_FORMAT)
                });

                const filteredSignedInVisitors = visitors.data
                    .filter((visitor: VisitorSearch) => {
                        return visitor.status === STATUS.SIGNED_IN.id;
                    })
                    .map((visitor: VisitorSearch) => {
                        return {
                            label: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
                            value: `${visitor.visitId}-${visitor.id}`, // Use visitor.visitId instead of visit.id
                            visitId: visitor.visitId, // Use visitId directly instead of the entire visit object
                            visitorId: visitor.id,
                        };
                    });
                setVisitorOptions(filteredSignedInVisitors);
            } catch (error) {
                console.log("Failed to fetch visitor options.");
            } finally {
                setLoading(false);
            }
        };
        fetchVisitorOptions();
    }, [open, orgId, globalUserId, locationIdRef]);

    const handleOptionSelect = async (value: string, option: VisitorOption) => {
        setIsProcessing(true);
        try {
            const selectedVisit = await getRequest(`/orgs/${orgId}/visitor/${option.visitId}`);
            dispatch(setSelectedVisit(selectedVisit.data[0]));
            openVisitorsDrawer();
            onClose();
        } catch (error) {
            console.error('Failed to process visitor selection:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Modal
            title={t('signOutModal.title')}
            open={open}
            onCancel={isProcessing ? undefined : onClose}
            footer={null}
            centered
            closable={!isProcessing}
        >
            <OpSpace direction="vertical" size="large" style={{ display: 'flex', paddingTop: '10px' }}>
                {loading || isProcessing ? (
                    <Loader />
                ) : (
                    <OpSelect
                        testId='select-visitors-to-sign-out'
                        options={visitorOptions}
                        placeholder={t('signOutModal.placeholder')}
                        onChange={(value, option) => handleOptionSelect(value as string, option as VisitorOption)}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        style={{ width: '100%' }}
                    />
                )}
            </OpSpace>
        </Modal>
    );
};

export default SignOutModal;
